import { Fragment, useCallback, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ArrowUpTrayIcon, XMarkIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import Papa from "papaparse";
import { MXCsvRecord, CsvAnalysis, CsvAnalyzeResponse, MultiEventEntry } from "./types";
import {
  mapCsvHeaders,
  analyzeCsv,
  formatCents,
  parseReferenceId,
  postStaged,
  detectProcessor,
  mapStripeRecords,
  STRIPE_REQUIRED_COLUMNS,
} from "./helpers";

const API_BASE =
  (import.meta as any).env?.VITE_RONIN_API_BASE ?? "https://api.roninpos.app";

/** One uploaded processor file with its parsed records and local analysis. */
interface ProcessorFile {
  fileName: string;
  processorName: string;
  records: MXCsvRecord[];
  analysis: CsvAnalysis;
  headerMapping: Record<string, string>;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onUpload: (records: MXCsvRecord[], selectedEventId: number, eventName: string, dateFrom?: string, dateTo?: string, processorNames?: string[]) => void;
  onUploadAll?: (events: MultiEventEntry[], processorNames: string[]) => void;
}

export function MXUploadModal({ open, onClose, onUpload, onUploadAll }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- Multi-file state ---
  const [files, setFiles] = useState<ProcessorFile[]>([]);

  // --- Server analysis (runs on combined records) ---
  const [serverAnalysis, setServerAnalysis] = useState<CsvAnalyzeResponse | null>(null);
  const [analyzing, setAnalyzing] = useState(false);

  // --- General state ---
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  const reset = useCallback(() => {
    setFiles([]);
    setServerAnalysis(null);
    setAnalyzing(false);
    setParsing(false);
    setError(null);
    setSelectedKeys(new Set());
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  // --- Server analysis against combined records ---
  const runServerAnalysis = useCallback(async (allRecords: MXCsvRecord[]) => {
    setAnalyzing(true);
    try {
      const token = localStorage.getItem("ron-auth");
      // Sent in pieces — one request can't carry a big event's rows under
      // Vercel's 4.5 MB cap (RP-2535, see helpers.tsx → postStaged).
      const result = await postStaged<CsvAnalyzeResponse>(
        API_BASE,
        "/api/mx-csv-analyze",
        allRecords.map(r => ({
          reference_id: parseReferenceId(r.invoice, r.memo),
          amount: r.amount,
          status: r.status,
          date: r.date,
        })),
        {},
        token
      );
      setServerAnalysis(result);
    } catch (err: any) {
      console.error("Server analysis failed:", err);
      // Say so on screen. This used to fail silently, leaving "Select events
      // to reconcile" greyed out with no reason given (RP-2535).
      setServerAnalysis(null);
      setError(`Couldn't match this file to Ronin events: ${err?.message || "unknown error"}`);
    } finally {
      setAnalyzing(false);
    }
  }, []);

  // --- Parse a dropped/selected file ---
  const handleFile = useCallback((file: File) => {
    setParsing(true);
    setError(null);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setParsing(false);
        const rawHeaders = results.meta.fields || [];

        // Auto-detect which processor exported this file.
        const kind = detectProcessor(rawHeaders);

        let records: MXCsvRecord[];
        let mapping: Record<string, string> = {};
        let processorName: string;

        if (kind === "stripe") {
          const headerSet = new Set(rawHeaders.map((h) => h.trim().toLowerCase()));
          const missing = STRIPE_REQUIRED_COLUMNS.filter((c) => !headerSet.has(c.toLowerCase()));
          if (missing.length > 0) {
            setError(`Stripe export is missing required columns: ${missing.join(", ")}`);
            return;
          }
          records = mapStripeRecords(results.data as Record<string, string>[], rawHeaders);
          processorName = "Stripe";
        } else {
          mapping = mapCsvHeaders(rawHeaders);
          const mappedFields = Object.values(mapping);
          const required = ["status", "amount"];
          const missing = required.filter((f) => !mappedFields.includes(f));
          if (missing.length > 0) {
            setError(`Missing required columns: ${missing.join(", ")}. Found: ${rawHeaders.join(", ")}`);
            return;
          }
          records = (results.data as Record<string, string>[]).map((row, idx) => {
            const mapped: Record<string, string> = {};
            for (const [rawHeader, normField] of Object.entries(mapping)) {
              mapped[normField] = row[rawHeader] || "";
            }
            return { ...mapped, _rowIndex: idx } as unknown as MXCsvRecord;
          });
          processorName = "MX Merchant";
        }

        const analysis = analyzeCsv(records);
        const newFile: ProcessorFile = {
          fileName: file.name,
          records,
          analysis,
          headerMapping: mapping,
          processorName,
        };
        setFiles(prev => {
          const updatedFiles = [...prev, newFile];
          const combined = updatedFiles.flatMap(f => f.records);
          runServerAnalysis(combined);
          return updatedFiles;
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
      },
      error: (err) => { setParsing(false); setError(`CSV parse error: ${err.message}`); },
    });
  }, [runServerAnalysis]);

  // --- Remove a file from the list ---
  const removeFile = useCallback((index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    setServerAnalysis(null);
    setSelectedKeys(new Set());
    if (updatedFiles.length > 0) {
      const combined = updatedFiles.flatMap(f => f.records);
      runServerAnalysis(combined);
    }
  }, [files, runServerAnalysis]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const allRecords = files.flatMap((f, fileIdx) => f.records.map(r => ({ ...r, _fileIndex: fileIdx })));
  const totalRows = files.reduce((s, f) => s + f.analysis.totalRows, 0);
  const totalAmount = files.reduce((s, f) => s + f.analysis.totalAmount, 0);

  const allEventKeys = (serverAnalysis?.events ?? []).map(e => `${e.event_id}-${e.year}`);
  const isAllSelected = allEventKeys.length > 0 && allEventKeys.every(k => selectedKeys.has(k));
  const selectedEvents = (serverAnalysis?.events ?? []).filter(
    (e) => selectedKeys.has(`${e.event_id}-${e.year}`)
  );

  const toggleKey = (key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    if (isAllSelected) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(allEventKeys));
    }
  };

  /** Build a MultiEventEntry for a given event from the server analysis */
  const buildEntry = (ev: CsvAnalyzeResponse["events"][number]): MultiEventEntry => {
    let filtered = allRecords;
    if (ev.mx_date_earliest && ev.mx_date_latest) {
      const rangeStart = new Date(ev.mx_date_earliest);
      const rangeEnd = new Date(ev.mx_date_latest);
      rangeStart.setDate(rangeStart.getDate() - 1);
      rangeEnd.setDate(rangeEnd.getDate() + 1);
      filtered = allRecords.filter((r) => {
        if (!r.date) return false;
        try {
          const d = new Date(r.date);
          return d >= rangeStart && d <= rangeEnd;
        } catch { return false; }
      });
    }
    const label = ev.year !== 'unknown'
      ? `${ev.event_name} (${ev.year})`
      : ev.event_name;
    const dateFrom = ev.mx_date_earliest
      ? new Date(new Date(ev.mx_date_earliest).getTime() - 86400000).toISOString()
      : undefined;
    const dateTo = ev.mx_date_latest
      ? new Date(new Date(ev.mx_date_latest).getTime() + 86400000).toISOString()
      : undefined;
    return { records: filtered, eventId: ev.event_id, eventName: label, dateFrom, dateTo };
  };

  const handleSubmit = () => {
    if (allRecords.length === 0 || selectedEvents.length === 0) return;
    const processorNames = [...new Set(files.map(f => f.processorName))];

    // --- Multi-event mode (2+ selected) ---
    if (selectedEvents.length > 1 && onUploadAll) {
      const entries = selectedEvents.map(buildEntry);
      onUploadAll(entries, processorNames);
      onClose();
      return;
    }

    // --- Single event mode ---
    const ev = selectedEvents[0];
    const entry = buildEntry(ev);
    onUpload(entry.records, entry.eventId, entry.eventName, entry.dateFrom, entry.dateTo, processorNames);
    onClose();
  };

  const formatEventDate = (d: string | null) => {
    if (!d) return "";
    try {
      return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch { return ""; }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4" enterTo="opacity-100 translate-y-0" leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0" leaveTo="opacity-0 translate-y-4">
              <Dialog.Panel className="relative w-full max-w-5xl rounded-lg bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
                <button className="absolute right-4 top-4 text-gray-400 hover:text-gray-500" onClick={onClose}>
                  <XMarkIcon className="h-5 w-5" />
                </button>
                <Dialog.Title className="text-lg font-semibold text-gray-900">Upload Payment Processor Reports</Dialog.Title>
                <p className="mt-1 text-sm text-gray-500">
                  Add one or more CSV exports from your payment processors — MX Merchant and Stripe (unified payments) are detected automatically. If this event uses multiple processors, add a file for each.
                </p>

                {/* Already-added files */}
                {files.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${f.processorName === "Stripe" ? "bg-indigo-100 text-indigo-800" : "bg-green-100 text-green-800"}`}>
                              {f.processorName}
                            </span>
                            <span className="text-sm text-gray-700 truncate">{f.fileName}</span>
                          </div>
                          <p className="mt-0.5 text-xs text-gray-500">
                            {f.analysis.totalRows.toLocaleString()} rows &middot; ${f.analysis.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            {f.analysis.dateRange && <> &middot; {f.analysis.dateRange.earliest} &mdash; {f.analysis.dateRange.latest}</>}
                          </p>
                        </div>
                        <button
                          className="text-gray-400 hover:text-red-500 p-1"
                          onClick={() => removeFile(i)}
                          title="Remove this file"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    {files.length > 1 && (
                      <div className="text-xs text-gray-500 pl-1">
                        Combined: {totalRows.toLocaleString()} rows &middot; ${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    )}
                  </div>
                )}

                {/* Drop zone — always visible to add more files */}
                {!parsing && (
                  <div
                    className="mt-4 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-6 hover:border-blue-400 cursor-pointer"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {files.length === 0 ? (
                      <ArrowUpTrayIcon className="h-10 w-10 text-gray-400" />
                    ) : (
                      <PlusIcon className="h-8 w-8 text-gray-400" />
                    )}
                    <p className="mt-2 text-sm text-gray-600">
                      {files.length === 0
                        ? "Drag & drop a CSV here, or click to browse"
                        : "Add another processor CSV"
                      }
                    </p>
                    <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFile(file); }} />
                  </div>
                )}

                {error && <div className="mt-3 rounded bg-red-50 p-3 text-sm text-red-700">{error}</div>}

                {/* Parsing indicator */}
                {parsing && (
                  <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-8 flex flex-col items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
                    <p className="mt-3 text-sm font-medium text-gray-700">Reading CSV file...</p>
                  </div>
                )}

                {/* Loading indicator for server analysis */}
                {analyzing && (
                  <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-8 flex flex-col items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
                    <p className="mt-3 text-sm font-medium text-gray-700">Analyzing records and matching to events...</p>
                    <p className="mt-1 text-xs text-gray-500">Looking up {totalRows.toLocaleString()} records against Ronin</p>
                  </div>
                )}

                {/* Server Event Breakdown */}
                {serverAnalysis && !analyzing && (
                  <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
                    <h3 className="text-sm font-semibold text-gray-900">Event Breakdown</h3>
                    <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-gray-600 mb-2">
                      <div>Unique Refs: <span className="font-semibold text-gray-900">{serverAnalysis.unique_reference_ids.toLocaleString()}</span></div>
                      <div>Matched to Ronin: <span className="font-semibold text-green-700">{serverAnalysis.matched_to_ronin.toLocaleString()}</span></div>
                      <div>Unmatched: <span className="font-semibold text-orange-600">{serverAnalysis.unmatched_reference_ids.toLocaleString()}</span></div>
                    </div>
                    {serverAnalysis.unparseable_rows > 0 && (
                      <p className="text-xs text-red-600 mb-2">
                        {serverAnalysis.unparseable_rows.toLocaleString()} rows could not be parsed (no reference ID found)
                      </p>
                    )}

                    <div className="overflow-x-auto border rounded max-h-64 overflow-y-auto">
                      <table className="min-w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">ID</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">Event</th>
                            <th className="px-3 py-2 text-center font-medium text-gray-700">Year</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-700">CSV Date Range</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-700">CSV Rows</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-700">CSV Total</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-700">Ronin Total</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-700">Refs</th>
                            <th className="px-3 py-2 text-center font-medium text-gray-700">Select</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Select All row */}
                          {onUploadAll && serverAnalysis.events.length > 1 && (
                            <tr
                              className={`border-t cursor-pointer hover:bg-blue-50 ${isAllSelected ? "bg-blue-100" : ""}`}
                              onClick={toggleAll}
                            >
                              <td className="px-3 py-2 text-gray-500">—</td>
                              <td className="px-3 py-2 font-semibold text-blue-700">Select All</td>
                              <td className="px-3 py-2 text-center">
                                <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800">
                                  {serverAnalysis.events.length}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-gray-500">—</td>
                              <td className="px-3 py-2 text-right">{serverAnalysis.events.reduce((s, e) => s + e.mx_rows, 0).toLocaleString()}</td>
                              <td className="px-3 py-2 text-right">{formatCents(serverAnalysis.events.reduce((s, e) => s + e.mx_total_cents, 0))}</td>
                              <td className="px-3 py-2 text-right">{formatCents(serverAnalysis.events.reduce((s, e) => s + e.ronin_total_cents, 0))}</td>
                              <td className="px-3 py-2 text-right">{serverAnalysis.events.reduce((s, e) => s + e.matched_refs, 0).toLocaleString()}</td>
                              <td className="px-3 py-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={isAllSelected}
                                  onChange={toggleAll}
                                  className="h-4 w-4 rounded text-blue-600"
                                />
                              </td>
                            </tr>
                          )}
                          {serverAnalysis.events.map((ev) => {
                            const key = `${ev.event_id}-${ev.year}`;
                            const isChecked = selectedKeys.has(key);
                            return (
                            <tr
                              key={key}
                              className={`border-t cursor-pointer hover:bg-blue-50 ${isChecked ? "bg-blue-100" : ""}`}
                              onClick={() => toggleKey(key)}
                            >
                              <td className="px-3 py-2 text-gray-500">{ev.event_id}</td>
                              <td className="px-3 py-2 font-medium text-gray-900">{ev.event_name}</td>
                              <td className="px-3 py-2 text-center">
                                <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800">
                                  {ev.year}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-gray-500">
                                {ev.mx_date_earliest ? formatEventDate(ev.mx_date_earliest) : "—"}
                                {ev.mx_date_latest ? ` – ${formatEventDate(ev.mx_date_latest)}` : ""}
                              </td>
                              <td className="px-3 py-2 text-right">{ev.mx_rows.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right">{formatCents(ev.mx_total_cents)}</td>
                              <td className="px-3 py-2 text-right">{formatCents(ev.ronin_total_cents)}</td>
                              <td className="px-3 py-2 text-right">{ev.matched_refs.toLocaleString()}</td>
                              <td className="px-3 py-2 text-center">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleKey(key)}
                                  className="h-4 w-4 rounded text-blue-600"
                                />
                              </td>
                            </tr>
                            );
                          })}
                          {serverAnalysis.unmatched_mx_rows > 0 && (
                            <tr className="border-t bg-gray-50">
                              <td className="px-3 py-2 text-gray-400">—</td>
                              <td className="px-3 py-2 font-medium text-gray-500 italic">Unmatched (no Ronin event)</td>
                              <td className="px-3 py-2 text-center text-gray-400">—</td>
                              <td className="px-3 py-2 text-gray-400">—</td>
                              <td className="px-3 py-2 text-right text-gray-500">{serverAnalysis.unmatched_mx_rows.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right text-gray-500">{formatCents(serverAnalysis.unmatched_mx_total_cents)}</td>
                              <td className="px-3 py-2 text-right text-gray-400">—</td>
                              <td className="px-3 py-2 text-right text-gray-500">{serverAnalysis.unmatched_reference_ids.toLocaleString()}</td>
                              <td className="px-3 py-2"></td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-6 flex justify-end gap-3">
                  <button className="rounded px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" onClick={() => { reset(); onClose(); }}>Cancel</button>
                  <button
                    className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    disabled={files.length === 0 || selectedEvents.length === 0 || !!error || analyzing}
                    onClick={handleSubmit}
                  >
                    {selectedEvents.length > 1
                      ? `Run Reconciliation — ${selectedEvents.length} Events`
                      : selectedEvents.length === 1
                        ? `Run Reconciliation — ${selectedEvents[0].year !== 'unknown' ? `${selectedEvents[0].event_name} (${selectedEvents[0].year})` : selectedEvents[0].event_name}`
                        : files.length === 0
                          ? "Add a CSV file to get started"
                          : "Select events to reconcile"
                    }
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}